// moms-harness:anchor v2
// Spec: docs/spec/auth/LOGISPRD-10053-okta-app-access-denied.yaml

import { useEffect, useRef } from 'react'
import { type NavigateFunction, useNavigate } from 'react-router-dom'
import { clearOktaTokens, ensureOktaAutoRenew, handleOktaCallback, isOktaConfigured } from '@/utils/okta/oktaAuth'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { PATHS } from '@/utils/routes/paths'
import { AUTH_ERROR_CODE, AUTH_MEMBERSHIP_ERROR_MESSAGE, HOME_PATH } from '@/utils/consts'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { VStack } from '@musinsa/mwms-ds'
import useLoginSuccessFlow from '@/pages/auth/useLoginSuccessFlow'
import { callToCheckLoginUser } from '@/services/auth/apis'

const OKTA_APP_UNASSIGNED_MARKER = 'not assigned to the client application'

// Spec: docs/spec/auth/SPEC-auth-client-auth-event-removal-A.yaml#callback-branches-preserved
const isOktaAccessDeniedError = (error: unknown): boolean => {
	if (error === null || typeof error !== 'object') return false
	const { errorCode, message } = error as { errorCode?: unknown; message?: unknown }
	const code = typeof errorCode === 'string' ? errorCode.toLowerCase() : ''
	const text = typeof message === 'string' ? message.toLowerCase() : ''
	return text.includes(OKTA_APP_UNASSIGNED_MARKER) || code === 'access_denied' || text.includes('access_denied')
}

const handleCallbackError = (error: unknown, navigate: NavigateFunction): void => {
	// LOGISPRD-9057: Okta 인증은 통과했으나 MOMS 멤버십 self-lookup 이 실패한 경우 분기한다.
	const errorCode = Utils.getApiErrorCode(error)

	// 비활성 계정 화면은 활성화 요청에 Okta Bearer가 필요하므로 토큰을 유지한다.
	if (errorCode === AUTH_ERROR_CODE.USER_INACTIVE) {
		navigate(PATHS['auth.deactivated'], { replace: true })
		return
	}
	if (errorCode === AUTH_ERROR_CODE.USER_ACTIVATION_PENDING) {
		navigate(`${PATHS['auth.deactivated']}?pending=true`, { replace: true })
		return
	}

	// 그 외 실패는 잔존 Okta 세션을 정리하되 Knox 저장소와의 기존 호환 동작은 유지한다.
	clearOktaTokens()
	AuthStorage.accessToken.clear()
	AuthStorage.authMode.clear()
	sessionStorage.removeItem('oktaReauthInflight')

	if (errorCode === AUTH_ERROR_CODE.USER_NOT_FOUND) {
		navigate(PATHS['auth.okta-signup'], { replace: true })
		return
	}
	if (errorCode === AUTH_ERROR_CODE.USER_NOT_APPROVED) {
		MAlert.show(AUTH_MEMBERSHIP_ERROR_MESSAGE[errorCode])
		navigate(PATHS['auth.login'], { replace: true })
		return
	}
	// LOGISPRD-10053: app-unassigned는 회원가입 완료가 아니라 현재 Okta 계정의 MOMS 접근 불가 상태다.
	if (isOktaAccessDeniedError(error)) {
		navigate(PATHS['auth.okta-access-denied'], { replace: true })
		return
	}

	const message = error instanceof Error ? error.message : String(error)
	MAlert.show(`Okta 로그인 처리 오류: ${message}`)
	navigate(PATHS['auth.login'], { replace: true })
}

/**
 * LOGISPRD-7099 — Okta 로그인 redirect callback (`/login/callback`).
 * code → token 교환 후 Okta Bearer 로 MOMS 사용자 정보를 조회해 세션을 세팅하고,
 * 기존 로그인 성공 플로우(onCheckAuthFlow)로 메인 진입한다.
 *
 * 사용자 정보 조회(`GET /api/auth/check`)는 게이트웨이 Okta 검증 + auth_code self-lookup
 * (BE §7.2 / LOGISPRD-7099 gateway dev bypass)을 통해 email 기준으로 MOMS 유저를 식별한다.
 */
function OktaLoginCallback() {
	const navigate = useNavigate()
	const { onCheckAuthFlow } = useLoginSuccessFlow()
	const ran = useRef(false)

	useEffect(() => {
		if (ran.current) return // 콜백 핸들러 1회만 실행
		ran.current = true

		// redirect 왕복 완료 → 401 재인증 가드 해제
		sessionStorage.removeItem('oktaReauthInflight')

		if (!isOktaConfigured()) {
			navigate(PATHS['auth.login'], { replace: true })
			return
		}
		;(async () => {
			try {
				await handleOktaCallback()
				// Okta 로그인 세션 확정 → 이후 request 는 Bearer(Okta) 사용
				AuthStorage.authMode.set('okta')
				// autoRenew 백그라운드 서비스 가동 (만료 전 자동 갱신)
				await ensureOktaAutoRenew()

				// dev: 토큰 확인용 성공 랜딩(REACT_APP_OKTA_DEV_LANDING=true). 그 외 실서비스 진입.
				if (process.env.REACT_APP_OKTA_DEV_LANDING === 'true') {
					navigate('/login/success', { replace: true })
					return
				}

				// Okta Bearer 로 MOMS 사용자 정보 조회(게이트웨이 검증 + self-lookup) → 세션 세팅
				// skipOktaReauth: 조회 401(미배포/MOMS 미가입 등)이 재인증 redirect 루프를 타지 않게 하고 아래 catch 로 처리.
				const user = await callToCheckLoginUser({ skipOktaReauth: true })
				AuthStorage.user.setByAuthChecker(user)

				// 기존 로그인 성공 플로우(OTP/비밀번호/부서 가드 → 메인) 재사용
				onCheckAuthFlow(() => {
					globalThis.location.href = HOME_PATH
				})
			} catch (e) {
				handleCallbackError(e, navigate)
			}
		})()
	}, [])

	return (
		<VStack block align="center" justify="center" style={{ height: '100vh' }}>
			로그인 처리 중...
		</VStack>
	)
}

export default OktaLoginCallback
